import React, { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
    HiOutlineHeart, HiHeart, HiOutlineMagnifyingGlass, HiOutlineSparkles,
    HiOutlineNoSymbol, HiOutlineChatBubbleLeftRight, HiOutlineFlag,
    HiOutlineXMark, HiOutlineUser, HiOutlineMapPin, HiOutlineBriefcase,
    HiOutlineAcademicCap, HiOutlineCalendarDays, HiOutlinePaperAirplane,
    HiOutlineArrowPath, HiOutlinePrinter, HiOutlineChevronLeft, HiOutlineChevronRight,
    HiOutlinePhoto, HiOutlineLockClosed, HiOutlinePhone, HiOutlineEnvelope,
    HiOutlineStar, HiOutlineUsers, HiOutlineAdjustmentsHorizontal, HiOutlineFunnel,
    HiOutlineCurrencyRupee, HiOutlineChevronDown, HiOutlineSquares2X2, HiOutlineListBullet,
} from "react-icons/hi2"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"
import API from "../../Common/API"
import Button from "../../Components/Button"
import ReportUserDialog from "../../Components/ReportUserDialog"
import { useLanguage } from "../../Common/LanguageContext"
import dummy from "../../Assets/Images/dummyProfile.jpeg"
import { rankMatchProfiles } from "../../Common/memberMatches"

const VERDICT_COLOR = {
    excellent: { bg: "bg-[#ecf8ef]", border: "border-[#cfe8d4]", text: "text-[#1a8a3a]" },
    good:      { bg: "bg-[#eef5ff]", border: "border-[#cfe1ff]", text: "text-[#225ec1]" },
    average:   { bg: "bg-[#fff8e6]", border: "border-[#f7e3a3]", text: "text-[#a07414]" },
    incompatible: { bg: "bg-[#fff5f5]", border: "border-[#efcaca]", text: "text-[#a03e3e]" },
}
const verdictColor = (v) => VERDICT_COLOR[v] || VERDICT_COLOR.average

/* ─── Filter constants ────────────────────────────────────────────────────── */
const DEFAULT_FILTERS = {
    ageMin:          "",
    ageMax:          "",
    city:            "",
    maritalStatus:   "",
    profession:      "",
    salaryRange:     "",
    star:            "",
    poruthamVerdict: "",
}

const SALARY_RANGES = [
    { label: "Any Income",    value: "" },
    { label: "Under ₹3 LPA", value: "u3" },
    { label: "₹3 – 5 LPA",   value: "3to5" },
    { label: "₹5 – 10 LPA",  value: "5to10" },
    { label: "₹10 – 25 LPA", value: "10to25" },
    { label: "₹25 LPA+",     value: "25plus" },
]

// Normalize salary text (e.g. "40000", "5 LPA", "5,00,000") → annual ₹
const parseSalaryAnnual = (raw) => {
    if (!raw) return null
    const s = String(raw).toLowerCase().replace(/,/g, "")
    const n = parseFloat(s.replace(/[^0-9.]/g, ""))
    if (isNaN(n)) return null
    if (s.includes("lpa") || s.includes("lakh") || s.includes("lac") || s.includes("l")) {
        return n * 100000   // convert lakhs → rupees
    }
    if (n < 100) return n * 100000  // treat bare tiny numbers as LPA
    if (n < 10000) return n * 12    // monthly → annual
    return n                        // already annual
}

const salaryInRange = (raw, range) => {
    if (!range) return true
    const annual = parseSalaryAnnual(raw)
    if (annual === null) return false
    if (range === "u3")    return annual < 300000
    if (range === "3to5")  return annual >= 300000 && annual < 500000
    if (range === "5to10") return annual >= 500000 && annual < 1000000
    if (range === "10to25")return annual >= 1000000 && annual < 2500000
    if (range === "25plus")return annual >= 2500000
    return true
}

const formatName = (m) => [m?.firstName, m?.lastName].filter(Boolean).join(" ") || m?.refId || "Member"

const isObjectId = (v) => typeof v === 'string' && /^[0-9a-f]{24}$/i.test(v)
const resolveText = (v) => (v && !isObjectId(v) ? v : null)

const getStar = (m) =>
    resolveText(m?.additionalInformation?.starName) ||
    resolveText(m?.additionalInformation?.star) ||
    resolveText(m?.starName) ||
    resolveText(m?.star) ||
    null

const getPhoto = (m) => {
    const imgs = m?.images?.length ? m.images : m?.previewUrls || []
    return imgs.find(i => i?.imageUrl)?.imageUrl || null
}

// Returns all non-empty image URLs for a profile
const getPhotos = (m) => {
    const imgs = m?.images?.length ? m.images : m?.previewUrls || []
    return imgs.map(i => i?.imageUrl).filter(Boolean)
}

/* ─── PhotoViewer ─────────────────────────────────────────────────────────── *
 * Shows the profile's photos in a swipeable carousel with thumbnail strip.
 * Falls back to a large dummy avatar when no photos exist.
 * A lightbox (full-screen overlay) opens when the main photo is clicked.
 * ─────────────────────────────────────────────────────────────────────────── */
const PhotoViewer = ({ profile }) => {
    const photos = getPhotos(profile)
    const name   = formatName(profile)
    const [active, setActive]   = useState(0)
    const [lightbox, setLightbox] = useState(false)

    // Reset to first photo whenever the profile changes
    useEffect(() => { setActive(0) }, [profile._id]) // eslint-disable-line

    const prev = () => setActive(i => (i - 1 + photos.length) % photos.length)
    const next = () => setActive(i => (i + 1) % photos.length)

    // No photos → large dummy avatar
    if (photos.length === 0) {
        const initials = (name[0] || "M").toUpperCase()
        const [bg, text] = avatarColor(profile?.firstName)
        return (
            <div className="relative w-full aspect-[4/3] flex items-center justify-center bg-slate-50 border-b border-borderColor overflow-hidden">
                <div className={`flex h-28 w-28 items-center justify-center rounded-full ${bg} ${text} text-5xl font-bold shadow-lg`}>
                    {initials}
                </div>
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-slate-500 backdrop-blur-sm">
                    <HiOutlinePhoto className="h-3.5 w-3.5" />
                    No photos
                </div>
            </div>
        )
    }

    return (
        <>
            {/* Main photo */}
            <div className="relative w-full aspect-[4/3] bg-slate-100 overflow-hidden border-b border-borderColor cursor-zoom-in select-none"
                onClick={() => setLightbox(true)}>
                <AnimatePresence mode="wait">
                    <motion.img
                        key={active}
                        src={photos[active]}
                        alt={`${name} photo ${active + 1}`}
                        className="h-full w-full object-cover"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        onError={(e) => { e.target.src = dummy }}
                    />
                </AnimatePresence>

                {/* Prev / Next arrows — only when > 1 photo */}
                {photos.length > 1 && (
                    <>
                        <button type="button" onClick={(e) => { e.stopPropagation(); prev() }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition">
                            <HiOutlineChevronLeft className="h-5 w-5" />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); next() }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition">
                            <HiOutlineChevronRight className="h-5 w-5" />
                        </button>
                    </>
                )}

                {/* Photo count pill */}
                <div className="absolute bottom-2 right-2 rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur-sm">
                    {active + 1} / {photos.length}
                </div>
            </div>

            {/* Thumbnail strip — only when > 1 photo */}
            {photos.length > 1 && (
                <div className="flex gap-2 overflow-x-auto px-4 py-2.5 bg-white border-b border-borderColor scrollbar-none">
                    {photos.map((url, idx) => (
                        <button key={idx} type="button" onClick={() => setActive(idx)}
                            className={`relative shrink-0 h-12 w-12 rounded-lg overflow-hidden border-2 transition ${
                                idx === active ? "border-primeColor shadow-sm" : "border-transparent opacity-60 hover:opacity-90"
                            }`}>
                            <img src={url} alt={`thumb ${idx + 1}`} className="h-full w-full object-cover"
                                onError={(e) => { e.target.src = dummy }} />
                        </button>
                    ))}
                </div>
            )}

            {/* Lightbox */}
            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setLightbox(false)}
                    >
                        {/* Close */}
                        <button type="button" onClick={() => setLightbox(false)}
                            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition">
                            <HiOutlineXMark className="h-6 w-6" />
                        </button>

                        {/* Prev / Next in lightbox */}
                        {photos.length > 1 && (
                            <>
                                <button type="button" onClick={(e) => { e.stopPropagation(); prev() }}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition">
                                    <HiOutlineChevronLeft className="h-6 w-6" />
                                </button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); next() }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition">
                                    <HiOutlineChevronRight className="h-6 w-6" />
                                </button>
                            </>
                        )}

                        <motion.img
                            key={active}
                            src={photos[active]}
                            alt={`${name} photo ${active + 1}`}
                            className="max-h-[88vh] max-w-[88vw] rounded-xl object-contain shadow-2xl"
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => { e.target.src = dummy }}
                        />

                        {/* Dots */}
                        {photos.length > 1 && (
                            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5">
                                {photos.map((_, idx) => (
                                    <button key={idx} type="button" onClick={(e) => { e.stopPropagation(); setActive(idx) }}
                                        className={`h-2 rounded-full transition-all ${idx === active ? "w-5 bg-white" : "w-2 bg-white/40"}`} />
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

const AVATAR_COLORS = [
    ["bg-[#fde8f0]","text-[#b5385a]"],["bg-[#e8f0fe]","text-[#2c5cc9]"],
    ["bg-[#e8faf0]","text-[#1a7a3c]"],["bg-[#fef3e8]","text-[#b56a1a]"],
    ["bg-[#f0e8fe]","text-[#6b2ca8]"],["bg-[#e8fefe]","text-[#1a7a7a]"],
]
const avatarColor = (name) => AVATAR_COLORS[(name || "M").charCodeAt(0) % AVATAR_COLORS.length]

// Returns true when the viewer has an active paid subscription
const isPaidViewer = (user) => {
    if (!user) return false
    const role = user.role || ""
    if (role === "Admin" || role === "Super_Admin") return true
    if (!user.isPaid) return false
    if (!user.planValidityDate) return true
    return new Date(user.planValidityDate) > new Date()
}

/* ─── InfoCard ───────────────────────────────────────────────────────────── *
 * Renders a white card with a section title and a list of icon+label+value rows.
 * Rows with falsy value are automatically filtered out.
 * ─────────────────────────────────────────────────────────────────────────── */
const InfoCard = ({ title, children }) => {
    const rows = (Array.isArray(children) ? children : [children]).filter(i => i?.value)
    if (rows.length === 0) return null
    return (
        <div className="rounded-2xl border border-borderColor bg-white p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{title}</p>
            {rows.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3">
                    <Icon className="mt-3.5 h-4 w-4 shrink-0 text-primeColor" />
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-tpColor/70">{label}</p>
                        <p className="text-sm font-semibold text-tColor break-words">{value}</p>
                    </div>
                </div>
            ))}
        </div>
    )
}

/* ─── LockedSection ──────────────────────────────────────────────────────── *
 * Renders a soft upgrade prompt in place of premium-only content.
 * ─────────────────────────────────────────────────────────────────────────── */
const LockedSection = ({ title, desc }) => {
    const { t } = useLanguage()
    return (
        <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50 p-5 flex flex-col items-center gap-2 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <HiOutlineLockClosed className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-sm font-bold text-amber-800">{title || t("upgradeTitle")}</p>
            <p className="text-xs text-amber-700 leading-relaxed max-w-xs">{desc}</p>
            <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-bold text-white">
                {t("upgradeNow")}
            </span>
        </div>
    )
}

/* ─── FilterChip ─────────────────────────────────────────────────────────── *
 * Small removable chip shown below the filter bar when a filter is active.
 * ─────────────────────────────────────────────────────────────────────────── */
const FilterChip = ({ label, onRemove }) => (
    <button type="button" onClick={onRemove}
        className="inline-flex items-center gap-1 rounded-full border border-primeColor/30 bg-primeSoft px-3 py-1 text-xs font-semibold text-primeColor hover:bg-primeColor hover:text-white transition">
        {label}
        <HiOutlineXMark className="h-3 w-3" />
    </button>
)

const ProfileAvatar = ({ profile, size = "lg" }) => {
    const photo = getPhoto(profile)
    const initials = (formatName(profile)[0] || "M").toUpperCase()
    const [bg, text] = avatarColor(profile?.firstName)
    const cls = size === "lg"
        ? "h-16 w-16 rounded-2xl text-xl font-bold"
        : "h-12 w-12 rounded-xl text-base font-bold"
    if (photo) return <img src={photo} alt={formatName(profile)} className={`${cls} object-cover`} />
    return (
        <div className={`${cls} ${bg} ${text} flex items-center justify-center shrink-0`}>
            {initials}
        </div>
    )
}

const ageFromDob = (dob) => {
    if (!dob) return null
    const d = new Date(dob)
    if (Number.isNaN(d.getTime())) return null
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
}

const PoruthamBadge = ({ porutham }) => {
    const { t } = useLanguage()
    if (!porutham) {
        return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">{t("starNotSet")}</span>
    }
    const c = verdictColor(porutham.verdict)
    return (
        <span className={`inline-flex items-center gap-2 rounded-full ${c.bg} ${c.border} border px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ${c.text}`}>
            <HiOutlineSparkles className="h-3.5 w-3.5" />
            {porutham.total}/{porutham.max} · {porutham.verdict}
        </span>
    )
}

const MatchCard = ({ profile, mode, shortlisted, onShortlist, onView, onComputePorutham, onBlock, onUnblock, onChat, onReport }) => {
    const { t } = useLanguage()
    const age = ageFromDob(profile.dateOfBirth)
    const star = getStar(profile)
    const edu = resolveText(profile.educationalQualification) || resolveText(profile.education)
    const occ = resolveText(profile.occupationDetail) || resolveText(profile.profession) || resolveText(profile.occupation)
    const city = profile.presentAddress?.city || profile.contactInformation?.presentAddress?.city || profile.city
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3 rounded-2xl border border-borderColor bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        >
            <div className="flex items-start gap-3">
                <ProfileAvatar profile={profile} size="lg" />
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-tpColor">{t("ref")} {profile.refId || "—"}</p>
                    <h3 className="mt-0.5 truncate text-base font-semibold text-tColor">{formatName(profile)}</h3>
                    <p className="mt-0.5 text-sm text-tpColor">
                        {[age && `${age} ${t("yrs")}`, profile.gender, star].filter(Boolean).join(" · ")}
                    </p>
                    {/* Extra detail row */}
                    {(edu || occ || city) && (
                        <p className="mt-1 text-xs text-tpColor/80 truncate">
                            {[edu, occ, city && `📍 ${city}`].filter(Boolean).join("  ·  ")}
                        </p>
                    )}
                </div>
                {mode !== "blocked" && (
                    <button
                        type="button"
                        onClick={() => onShortlist(profile)}
                        aria-label={shortlisted ? t("removeShortlist") : t("addShortlist")}
                        title={shortlisted ? t("removeShortlist") : t("addShortlist")}
                        className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                            shortlisted
                                ? "border-[#C8920A] bg-[#FDF0C8] text-[#A07008]"
                                : "border-[#C8920A]/40 bg-white text-[#C8920A] hover:bg-[#FDF0C8] hover:border-[#C8920A]"
                        }`}
                    >
                        {shortlisted ? <HiHeart className="h-3.5 w-3.5" /> : <HiOutlineHeart className="h-3.5 w-3.5" />}
                        {shortlisted ? t("removeShortlist") || "Shortlisted" : t("addShortlist") || "Shortlist"}
                    </button>
                )}
            </div>

            {mode !== "blocked" && <PoruthamBadge porutham={profile.porutham} />}

            {/* Porutham chips — only for paid viewers (backend populates breakdown only for paid) */}
            {mode !== "blocked" && profile.porutham?.breakdown && (
                <div className="grid grid-cols-2 gap-1 text-xs text-tpColor sm:grid-cols-5">
                    {profile.porutham.breakdown.map((p) => (
                        <div key={p.name} className={`rounded-md px-2 py-1 ${p.score ? "bg-[#ecf8ef] text-[#1a8a3a]" : "bg-[#fff5f5] text-[#a03e3e]"}`}>
                            {p.name}
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
                {mode === "blocked" ? (
                    <Button type="primary" injectClass="text-xs" onClick={() => onUnblock(profile)}>{t("unblock")}</Button>
                ) : (
                    <>
                        <Button type="secondary" injectClass="text-xs" onClick={() => onView(profile)} title={t("viewProfile")}>{t("viewProfile")}</Button>
                        <Button type="secondary" injectClass="text-xs" onClick={() => onComputePorutham(profile)} title={t("detailedPorutham")}>{t("detailedPorutham")}</Button>
                        <button
                            type="button"
                            onClick={() => onChat(profile)}
                            title={t("chatBtn")}
                            className="inline-flex items-center gap-1 rounded-full border border-[#cfe1ff] bg-[#eef5ff] px-3 py-1 text-xs font-bold text-[#225ec1] hover:border-[#225ec1]"
                        >
                            <HiOutlineChatBubbleLeftRight className="h-4 w-4" />
                            {t("chatBtn")}
                        </button>
                        <button
                            type="button"
                            onClick={() => onReport(profile)}
                            className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#efcaca] bg-white px-3 py-1 text-xs font-bold text-[#a03e3e] hover:bg-[#fff5f5]"
                            title={t("reportTip")}
                        >
                            <HiOutlineFlag className="h-4 w-4" />
                            {t("report")}
                        </button>
                        <button
                            type="button"
                            onClick={() => onBlock(profile)}
                            className="inline-flex items-center gap-1 rounded-full border border-[#efcaca] bg-[#fff5f5] px-3 py-1 text-xs font-bold text-[#a03e3e] hover:border-[#a03e3e]"
                            title={t("notInterestedTip")}
                        >
                            <HiOutlineNoSymbol className="h-4 w-4" />
                            {t("notInterested")}
                        </button>
                    </>
                )}
            </div>
        </motion.div>
    )
}

/* ─── ClassicMatchCard ───────────────────────────────────────────────────── *
 * Full-width horizontal card with large photo on the left.
 * Gives more photo real estate while keeping all actions accessible.
 * ─────────────────────────────────────────────────────────────────────────── */
const ClassicMatchCard = ({ profile, mode, shortlisted, onShortlist, onView, onComputePorutham, onBlock, onUnblock, onChat, onReport }) => {
    const { t } = useLanguage()
    const photos = getPhotos(profile)
    const photo  = photos[0] || null
    const age    = ageFromDob(profile.dateOfBirth)
    const star   = getStar(profile)
    const edu    = resolveText(profile.educationalQualification) || resolveText(profile.education)
    const occ    = resolveText(profile.occupationDetail) || resolveText(profile.profession) || resolveText(profile.occupation)
    const city   = profile.presentAddress?.city || profile.contactInformation?.presentAddress?.city || profile.city
    const name   = formatName(profile)
    const initials = (name[0] || "M").toUpperCase()
    const [bg, text] = avatarColor(profile?.firstName)
    const [imgIdx, setImgIdx] = useState(0)
    const [lightbox, setLightbox] = useState(false)

    // cycle photos on arrow click
    const prevPhoto = (e) => { e.stopPropagation(); setImgIdx(i => (i - 1 + photos.length) % photos.length) }
    const nextPhoto = (e) => { e.stopPropagation(); setImgIdx(i => (i + 1) % photos.length) }
    const openLightbox = (e) => { e.stopPropagation(); if (photos.length > 0) setLightbox(true) }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex rounded-2xl border border-borderColor bg-white shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition"
        >
            {/* ── Left: large photo panel ── */}
            <div className="relative shrink-0 w-44 sm:w-52 bg-slate-100 select-none">
                {photos.length > 0 ? (
                    <>
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={imgIdx}
                                src={photos[imgIdx]}
                                alt={name}
                                className="h-full w-full object-cover cursor-pointer"
                                style={{ minHeight: "200px" }}
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                onError={(e) => { e.target.src = dummy }}
                                onClick={openLightbox}
                                title="Click to view photo"
                            />
                        </AnimatePresence>
                        {photos.length > 1 && (
                            <>
                                <button type="button" onClick={prevPhoto}
                                    className="absolute left-1 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition">
                                    <HiOutlineChevronLeft className="h-4 w-4" />
                                </button>
                                <button type="button" onClick={nextPhoto}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition">
                                    <HiOutlineChevronRight className="h-4 w-4" />
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                    {photos.map((_, i) => (
                                        <button key={i} type="button" onClick={(e) => { e.stopPropagation(); setImgIdx(i) }}
                                            className={`h-1.5 rounded-full transition-all ${i === imgIdx ? "w-4 bg-white" : "w-1.5 bg-white/50"}`} />
                                    ))}
                                </div>
                            </>
                        )}
                        {/* Expand icon hint */}
                        <div className="absolute bottom-2 right-2 pointer-events-none flex h-6 w-6 items-center justify-center rounded-full bg-black/40 text-white">
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                            </svg>
                        </div>
                    </>
                ) : (
                    <div className={`flex h-full min-h-[200px] items-center justify-center ${bg} ${text} text-4xl font-bold`}>
                        {initials}
                    </div>
                )}

                {/* Lightbox */}
                {lightbox && (
                    <div
                        className="fixed inset-0 z-[999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
                        onClick={() => setLightbox(false)}
                    >
                        {/* Close button */}
                        <button type="button" onClick={() => setLightbox(false)}
                            className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        {/* Prev / Next */}
                        {photos.length > 1 && (
                            <>
                                <button type="button" onClick={(e) => { e.stopPropagation(); prevPhoto(e) }}
                                    className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition">
                                    <HiOutlineChevronLeft className="h-6 w-6" />
                                </button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); nextPhoto(e) }}
                                    className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition">
                                    <HiOutlineChevronRight className="h-6 w-6" />
                                </button>
                            </>
                        )}
                        {/* Main lightbox image */}
                        <img
                            src={photos[imgIdx]}
                            alt={`${name} photo ${imgIdx + 1}`}
                            className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => { e.target.src = dummy }}
                        />
                        {/* Photo counter + name */}
                        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                            <p className="text-white font-semibold text-sm">{name}</p>
                            {photos.length > 1 && (
                                <div className="flex gap-1.5">
                                    {photos.map((_, i) => (
                                        <button key={i} type="button" onClick={(e) => { e.stopPropagation(); setImgIdx(i) }}
                                            className={`h-2 rounded-full transition-all ${i === imgIdx ? "w-6 bg-white" : "w-2 bg-white/40"}`} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Photo count badge */}
                {photos.length > 1 && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-bold text-white">
                        <HiOutlinePhoto className="h-3 w-3" />
                        {photos.length}
                    </div>
                )}

                {/* Shortlist heart overlay */}
                {mode !== "blocked" && (
                    <button type="button" onClick={() => onShortlist(profile)}
                        className={`absolute top-2 left-2 flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold backdrop-blur-sm transition ${
                            shortlisted
                                ? "border-[#C8920A] bg-[#FDF0C8]/90 text-[#A07008]"
                                : "border-white/60 bg-black/35 text-white hover:bg-black/55"
                        }`}
                        title={shortlisted ? t("removeShortlist") : t("addShortlist")}>
                        {shortlisted ? <HiHeart className="h-3.5 w-3.5" /> : <HiOutlineHeart className="h-3.5 w-3.5" />}
                        {shortlisted ? "Shortlisted" : "Shortlist"}
                    </button>
                )}
            </div>

            {/* ── Right: details panel ── */}
            <div className="flex flex-1 flex-col min-w-0 p-4 gap-3">
                {/* Name + ref */}
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-tpColor">{t("ref")} {profile.refId || "—"}</p>
                    <h3 className="text-lg font-bold text-tColor leading-tight">{name}</h3>
                    <p className="mt-0.5 text-base text-tpColor">
                        {[age && `${age} ${t("yrs")}`, profile.gender, star].filter(Boolean).join(" · ")}
                    </p>
                </div>

                {/* Key details grid */}
                <div className="grid grid-cols-1 gap-1.5 text-sm text-slate-600">
                    {edu && (
                        <div className="flex items-center gap-2">
                            <HiOutlineAcademicCap className="h-4 w-4 shrink-0 text-primeColor" />
                            <span className="truncate">{edu}</span>
                        </div>
                    )}
                    {occ && (
                        <div className="flex items-center gap-2">
                            <HiOutlineBriefcase className="h-4 w-4 shrink-0 text-primeColor" />
                            <span className="truncate">{occ}</span>
                        </div>
                    )}
                    {(profile.salary || profile.income) && (
                        <div className="flex items-center gap-2">
                            <HiOutlineCurrencyRupee className="h-4 w-4 shrink-0 text-primeColor" />
                            <span className="truncate">{profile.salary || profile.income}</span>
                        </div>
                    )}
                    {city && (
                        <div className="flex items-center gap-2">
                            <HiOutlineMapPin className="h-4 w-4 shrink-0 text-primeColor" />
                            <span className="truncate">{city}</span>
                        </div>
                    )}
                    {(profile.religion || profile.caste) && (
                        <div className="flex items-center gap-2">
                            <HiOutlineUser className="h-4 w-4 shrink-0 text-primeColor" />
                            <span className="truncate">{[profile.religion, profile.caste].filter(Boolean).join(" · ")}</span>
                        </div>
                    )}
                </div>

                {/* Porutham badge */}
                {mode !== "blocked" && <PoruthamBadge porutham={profile.porutham} />}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                    {mode === "blocked" ? (
                        <Button type="primary" injectClass="text-xs" onClick={() => onUnblock(profile)}>{t("unblock")}</Button>
                    ) : (
                        <>
                            <Button type="secondary" injectClass="text-xs" onClick={() => onView(profile)}>{t("viewProfile")}</Button>
                            <button type="button" onClick={() => onShortlist(profile)}
                                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition ${
                                    shortlisted
                                        ? "border-[#C8920A] bg-[#FDF0C8] text-[#A07008]"
                                        : "border-[#C8920A]/40 bg-white text-[#C8920A] hover:bg-[#FDF0C8] hover:border-[#C8920A]"
                                }`}>
                                {shortlisted ? <HiHeart className="h-3.5 w-3.5" /> : <HiOutlineHeart className="h-3.5 w-3.5" />}
                                {shortlisted ? "Shortlisted" : "Shortlist"}
                            </button>
                            <button type="button" onClick={() => onChat(profile)}
                                className="inline-flex items-center gap-1 rounded-full border border-[#cfe1ff] bg-[#eef5ff] px-3 py-1 text-xs font-bold text-[#225ec1] hover:border-[#225ec1]">
                                <HiOutlineChatBubbleLeftRight className="h-3.5 w-3.5" />
                                {t("chatBtn")}
                            </button>
                            <button type="button" onClick={() => onComputePorutham(profile)}
                                className="inline-flex items-center gap-1 rounded-full border border-borderColor bg-white px-3 py-1 text-xs font-bold text-tColor hover:border-primeColor hover:text-primeColor transition">
                                <HiOutlineSparkles className="h-3.5 w-3.5" />
                                {t("detailedPorutham")}
                            </button>
                            <div className="flex gap-1 ml-auto">
                                <button type="button" onClick={() => onReport(profile)}
                                    className="inline-flex items-center gap-1 rounded-full border border-[#efcaca] bg-white px-2.5 py-1 text-xs font-bold text-[#a03e3e] hover:bg-[#fff5f5]">
                                    <HiOutlineFlag className="h-3.5 w-3.5" />
                                </button>
                                <button type="button" onClick={() => onBlock(profile)}
                                    className="inline-flex items-center gap-1 rounded-full border border-[#efcaca] bg-[#fff5f5] px-2.5 py-1 text-xs font-bold text-[#a03e3e] hover:border-[#a03e3e]">
                                    <HiOutlineNoSymbol className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    )
}

const MemberMatches = () => {
    const history = useHistory()
    const userDetails   = useSelector((state) => state.auth.userDetails)
    const occupationOpts = useSelector((state) => state.auth.occupation || [])  // [{text, value}]
    const { t, lang } = useLanguage()

    // Resolve an expectedOccupation array (of IDs or {text,value} objects) → display string
    const resolveOccupation = (val) => {
        if (!val) return ''
        const arr = Array.isArray(val) ? val : [val]
        return arr.map(item => {
            if (typeof item === 'string') {
                const found = occupationOpts.find(o => o.value === item || o.value?.toString() === item)
                return found ? found.text : item
            }
            return item?.text || item?.label || item?.value || ''
        }).filter(Boolean).join(', ')
    }
    const amIPaid = isPaidViewer(userDetails)
    const [tab, setTab] = useState("browse")
    const [suggestions, setSuggestions] = useState([])
    const [shortlist, setShortlist] = useState([])
    const [blocked, setBlocked] = useState([])
    const [fetchError, setFetchError] = useState(null)
    const [incompleteProfile, setIncompleteProfile] = useState(false)
    const [pendingApproval, setPendingApproval] = useState(false)
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [viewMode, setViewMode] = useState("classic") // "grid" | "classic"
    const [poruthamModal, setPoruthamModal] = useState(null)
    const [reportTarget, setReportTarget] = useState(null)
    const [viewingProfile, setViewingProfile] = useState(null)
    const [slideTab, setSlideTab] = useState("profile")
    const [slidePorutham, setSlidePorutham] = useState(null)
    const [slidePoruthamLoading, setSlidePoruthamLoading] = useState(false)
    const [slideMessages, setSlideMessages] = useState([])
    const [slideSendText, setSlideSendText] = useState("")
    const [slideSending, setSlideSending] = useState(false)
    const [slideChatLoading, setSlideChatLoading] = useState(false)
    const chatEndRef = useRef(null)

    // Tamil name for the currently-viewed profile
    const [slideTamilName, setSlideTamilName] = useState("")
    useEffect(() => {
        if (lang !== 'ta' || !viewingProfile?._id) { setSlideTamilName(""); return }
        const xlit = (text) =>
            text ? API.post('/common/translate', { text })
                .then(r => r.data?.data?.translatedText || text).catch(() => text)
                : Promise.resolve('')
        // Use saved tFirstName/tLastName if admin stored them
        const fn = (viewingProfile.tFirstName && viewingProfile.tFirstName !== viewingProfile.firstName)
            ? Promise.resolve(viewingProfile.tFirstName) : xlit(viewingProfile.firstName)
        const ln = xlit(viewingProfile.lastName)
        Promise.all([fn, ln]).then(([first, last]) =>
            setSlideTamilName([first, last].filter(Boolean).join(' ') || viewingProfile.firstName || '')
        )
    }, [lang, viewingProfile?._id]) // eslint-disable-line

    // Suppress the browser's "Reload site? Changes you made may not be saved."
    // dialog — some browsers show it when form inputs have typed content
    // (e.g. the chat box in the slide-over).  Intercept at capture phase
    // and clear returnValue so the browser never shows the dialog.
    useEffect(() => {
        const suppress = (e) => { delete e.returnValue }
        window.addEventListener("beforeunload", suppress, { capture: true })
        return () => window.removeEventListener("beforeunload", suppress, { capture: true })
    }, [])

    const shortlistedIds = useMemo(() => new Set(shortlist.map((s) => s._id)), [shortlist])

    // Prevent concurrent fetchAll calls — rapid shortlist/block clicks would otherwise
    // cause multiple simultaneous reads + writes on the backend db.json.
    const fetchingRef = useRef(false)

    const fetchAll = async () => {
        if (fetchingRef.current) return   // already in flight
        fetchingRef.current = true
        setLoading(true)
        try {
            const [s, sl, bl] = await Promise.all([
                API.get("/matches/getSuggestions", { params: { limit: 50 } }),
                API.get("/matches/getShortlist"),
                API.get("/matches/getBlockList"),
            ])
            setSuggestions(s.data?.data || [])
            setIncompleteProfile(!!s.data?.incompleteProfile)
            setPendingApproval(!!s.data?.pendingApproval)
            setShortlist(sl.data?.data || [])
            setBlocked(bl.data?.data || [])
            setFetchError(null)
        } catch (err) {
            console.error("matches fetch failed", err?.response?.data || err)
            if (!err?.response) {
                setFetchError("Cannot reach the server. Make sure the backend is running on port 5001.")
            } else {
                setFetchError(`Error loading profiles (${err.response.status}). Please refresh.`)
            }
        } finally {
            setLoading(false)
            fetchingRef.current = false
        }
    }

    useEffect(() => { fetchAll() }, [])

    // Lock body scroll while slide-over is open; reset tab state on profile change.
    // Body overflow is unlocked in onExitComplete (AnimatePresence) so it only
    // restores after the panel is fully gone, not mid-animation.
    useEffect(() => {
        if (viewingProfile) {
            document.body.style.overflow = "hidden"
            setSlideTab("profile")
            setSlidePorutham(null)
            setSlideMessages([])
            setSlideSendText("")
        }
        // cleanup — also covers unmount / hard navigation
        return () => { document.body.style.overflow = "" }
    }, [viewingProfile?._id]) // eslint-disable-line

    // Load porutham detail when Porutham tab opens (paid users only)
    useEffect(() => {
        if (slideTab !== "porutham" || !viewingProfile) return
        if (!amIPaid) return  // free users see lock screen — no API call needed
        if (slidePorutham) return // already loaded for this profile
        const myId = userDetails?._id
        const profileIsMale = viewingProfile.gender?.toLowerCase().startsWith("m") ||
            viewingProfile.profileType?.toLowerCase() === "groom"
        const groomUserId = profileIsMale ? viewingProfile._id : myId
        const brideUserId  = profileIsMale ? myId : viewingProfile._id
        setSlidePoruthamLoading(true)
        API.post("/matches/computePorutham", { groomUserId, brideUserId })
            .then(res => setSlidePorutham({ result: res.data?.data }))
            .catch(err => setSlidePorutham({ error: err?.response?.data?.error || "Could not compute porutham" }))
            .finally(() => setSlidePoruthamLoading(false))
    }, [slideTab]) // eslint-disable-line

    // Load chat messages when Chat tab opens; poll every 3s while open
    useEffect(() => {
        if (slideTab !== "chat" || !viewingProfile) return
        let alive = true
        const load = () => {
            setSlideChatLoading(prev => slideMessages.length === 0 ? true : prev)
            API.get(`/chat/messages/${viewingProfile._id}`)
                .then(res => { if (alive) { setSlideMessages(res.data?.data?.messages || []); setSlideChatLoading(false) } })
                .catch(() => { if (alive) setSlideChatLoading(false) })
        }
        load()
        const id = setInterval(load, 3000)
        return () => { alive = false; clearInterval(id) }
    }, [slideTab, viewingProfile?._id]) // eslint-disable-line

    // Auto-scroll chat to bottom
    useEffect(() => {
        if (slideTab === "chat" && chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" })
        }
    }, [slideMessages.length, slideTab])

    const slideSend = async (e) => {
        e?.preventDefault?.()
        if (!viewingProfile || !slideSendText.trim() || slideSending) return
        const body = slideSendText
        setSlideSendText("")
        setSlideSending(true)
        try {
            await API.post("/chat/send", { toUserId: viewingProfile._id, message: body })
            const res = await API.get(`/chat/messages/${viewingProfile._id}`)
            setSlideMessages(res.data?.data?.messages || [])
        } catch { setSlideSendText(body) }
        finally { setSlideSending(false) }
    }

    const toggleShortlist = async (profile) => {
        try {
            if (shortlistedIds.has(profile._id)) {
                await API.post("/matches/removeShortlist", { userId: profile._id })
            } else {
                await API.post("/matches/addShortlist", { userId: profile._id })
            }
            await fetchAll()
        } catch (err) { console.error(err?.response?.data || err) }
    }

    const blockProfile = async (profile) => {
        try {
            await API.post("/matches/blockUser", { userId: profile._id })
            await fetchAll()
        } catch (err) { console.error(err?.response?.data || err) }
    }
    const unblockProfile = async (profile) => {
        try {
            await API.post("/matches/unblockUser", { userId: profile._id })
            await fetchAll()
        } catch (err) { console.error(err?.response?.data || err) }
    }

    const viewProfile = (profile) => {
        setViewingProfile(profile)
        // Notify the profile owner that someone viewed them (fire-and-forget, once per day per pair)
        try { API.post('/matches/trackView', { userId: profile._id }) } catch (_) {}
    }
    const openChat = (profile) => history.push(`/member/chat/${profile._id}`)

    const computeDetailedPorutham = async (profile) => {
        // Determine groom/bride based on the matched profile's gender;
        // the logged-in member is the opposite gender.
        const myId = userDetails?._id
        const profileIsMale = profile.gender?.toLowerCase().startsWith("m") ||
            profile.profileType?.toLowerCase() === "groom"
        const groomUserId = profileIsMale ? profile._id : myId
        const brideUserId  = profileIsMale ? myId : profile._id

        try {
            const res = await API.post("/matches/computePorutham", { groomUserId, brideUserId })
            setPoruthamModal({ profile, result: res.data?.data })
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || "Could not compute porutham"
            setPoruthamModal({ profile, error: msg })
        }
    }

    const escHtml = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')

    const printProfile = (profile) => {
        const star = getStar(profile)
        const age  = ageFromDob(profile.dateOfBirth)
        const rows = [
            ["Ref ID",          profile.refId],
            ["Gender",          profile.gender || profile.profileType],
            ["Age",             age ? `${age} years` : null],
            ["Date of Birth",   profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null],
            ["Star (Nakshatra)",star],
            ["Religion",        profile.religion],
            ["Caste",           profile.caste],
            ["Marital Status",  profile.maritalStatus],
            ["Height",          profile.heightInFeet || profile.height],
            ["Education",       profile.educationalQualification || profile.education],
            ["Occupation",      profile.occupationDetail || profile.profession],
            ["Salary",          profile.salary],
            ["Native",          profile.native],
            ["Current City",    profile.presentAddress?.city || profile.contactInformation?.presentAddress?.city || profile.city],
            ["Expectations",    profile.additionalInformation?.expectation],
        ].filter(([, v]) => v)

        const html = `<!DOCTYPE html><html><head><title>Profile – ${escHtml(formatName(profile))}</title>
<style>
  body{font-family:sans-serif;padding:32px;color:#1a1a2e;max-width:600px;margin:auto}
  h1{font-size:22px;margin-bottom:4px}
  .sub{color:#666;font-size:13px;margin-bottom:24px}
  table{width:100%;border-collapse:collapse}
  td{padding:8px 10px;font-size:14px;border-bottom:1px solid #eee}
  td:first-child{color:#888;font-weight:600;width:42%}
  td:last-child{font-weight:600;color:#1a1a2e}
  .badge{display:inline-block;background:#fff0f0;color:#c04a4a;padding:2px 10px;border-radius:99px;font-size:11px;margin-bottom:20px}
  @media print{body{padding:16px}}
</style></head><body>
<h1>${escHtml(formatName(profile))}</h1>
<div class="sub">Royal Kavery Matrimony · Ref ${escHtml(profile.refId || "—")}</div>
<table>${rows.map(([l, v]) => `<tr><td>${escHtml(l)}</td><td>${escHtml(v)}</td></tr>`).join("")}</table>
<p style="margin-top:24px;font-size:11px;color:#aaa">Printed from Royal Kavery Matrimony Member Portal</p>
</body></html>`
        const w = window.open("", "_blank", "width=680,height=800")
        if (!w) return
        w.document.write(html)
        w.document.close()
        w.focus()
        setTimeout(() => { w.print() }, 400)
    }

    // My Matches: ranked by compatibility score
    const myMatches = useMemo(() => rankMatchProfiles(userDetails, suggestions), [userDetails, suggestions])

    const list = tab === "browse"
        ? suggestions
        : tab === "myMatches"
            ? myMatches
            : tab === "shortlist"
                ? shortlist
                : blocked

    // Unique dropdown values — always built from all suggestions so options don't shrink as filters narrow
    const uniqueValues = useMemo(() => {
        const src = suggestions
        const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort()
        return {
            cities:          uniq(src.map(m => m.presentAddress?.city || m.contactInformation?.presentAddress?.city || m.city)),
            maritalStatuses: uniq(src.map(m => m.maritalStatus)),
            professions:     uniq(src.map(m => m.occupationDetail || m.profession)),
            stars:           uniq(src.map(m => getStar(m))),
            verdicts:        uniq(src.map(m => m.porutham?.verdict).filter(v => v && v !== "unknown")),
        }
    }, [suggestions])

    const activeFilterCount = useMemo(() =>
        Object.values(filters).filter(v => v !== "").length
    , [filters])

    const filtered = useMemo(() => {
        let result = list

        // Text search
        if (search.trim()) {
            const q = search.toLowerCase()
            result = result.filter((m) => {
                const hay = [m.firstName, m.lastName, m.mobileNumber, m.refId, m.starName, m.star,
                    m.occupationDetail, m.profession, m.city, m.religion].filter(Boolean).join(" ").toLowerCase()
                return hay.includes(q)
            })
        }

        const { ageMin, ageMax, city, maritalStatus, profession, salaryRange, star, poruthamVerdict } = filters

        // Age range
        if (ageMin !== "" || ageMax !== "") {
            result = result.filter((m) => {
                const age = ageFromDob(m.dateOfBirth)
                if (age === null) return false
                if (ageMin !== "" && age < parseInt(ageMin, 10)) return false
                if (ageMax !== "" && age > parseInt(ageMax, 10)) return false
                return true
            })
        }

        // City
        if (city) {
            const q = city.toLowerCase()
            result = result.filter((m) => {
                const c = (m.presentAddress?.city || m.contactInformation?.presentAddress?.city || m.city || "").toLowerCase()
                return c.includes(q)
            })
        }

        // Marital status
        if (maritalStatus) {
            result = result.filter(m => (m.maritalStatus || "").toLowerCase() === maritalStatus.toLowerCase())
        }

        // Profession (partial match)
        if (profession) {
            const q = profession.toLowerCase()
            result = result.filter(m => (m.occupationDetail || m.profession || "").toLowerCase().includes(q))
        }

        // Salary range
        if (salaryRange) {
            result = result.filter(m => salaryInRange(m.salary || m.income, salaryRange))
        }

        // Star / Nakshatra
        if (star) {
            result = result.filter(m => (getStar(m) || "").toLowerCase() === star.toLowerCase())
        }

        // Porutham verdict (my analysis — filter by compatibility score)
        if (poruthamVerdict) {
            result = result.filter(m => (m.porutham?.verdict || "").toLowerCase() === poruthamVerdict.toLowerCase())
        }

        return result
    }, [list, search, filters]) // eslint-disable-line

    return (
        <>
        <motion.div className="space-y-4">

            {/* ── Page header ── */}
            <div className="flex flex-col gap-1">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">{t("findYourMatch") || "Find Your Match"}</p>
                <h2 className="text-2xl font-semibold text-tColor">
                    {tab === "myMatches"
                        ? (t("myMatches") || "My Matches")
                        : tab === "shortlist"
                            ? t("shortlist")
                            : tab === "blocked"
                                ? t("blocked")
                                : (t("browseProfiles") || "Browse Profiles")}
                </h2>
                <p className="text-sm text-tpColor">
                    {tab === "myMatches"
                        ? "Virtually matched profiles ranked by compatibility score."
                        : "Profiles with photos — opposite gender members registered with us."}
                </p>
            </div>

            {/* ── Backend error banner ── */}
            {fetchError && (
                <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
                    <span>⚠️ {fetchError}</span>
                    <button type="button" onClick={fetchAll}
                        className="ml-4 rounded-xl border border-red-300 bg-white px-3 py-1.5 text-xs font-bold hover:border-red-500 transition">
                        Retry
                    </button>
                </div>
            )}

            {/* ── Pending approval banner ── */}
            {pendingApproval && !fetchError && !incompleteProfile && (
                <div className="flex flex-col gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-6 py-5">
                    <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-2xl">⏳</span>
                        <div>
                            <p className="text-sm font-bold text-blue-800">
                                {t('pendingApprovalTitle') || 'உங்கள் சுயவிவரம் அனுமதிக்கப்படவில்லை · Profile Pending Approval'}
                            </p>
                            <p className="mt-1 text-sm leading-relaxed text-blue-700">
                                {t('pendingApprovalDesc') || 'உங்கள் கணக்கை நிர்வாகி ஆய்வு செய்கிறார். அனுமதிக்கப்பட்ட பிறகு பொருத்தங்கள் காண்பிக்கப்படும். Your account is under review by the admin. Matches will be shown once approved.'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => window.location.href = '/member/profile'}
                        className="w-fit rounded-full bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
                    >
                        {t('goToProfile') || 'சுயவிவரம் நிரப்பு · Complete Profile →'}
                    </button>
                </div>
            )}

            {/* ── Incomplete profile banner ── */}
            {incompleteProfile && !fetchError && (
                <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5">
                    <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-2xl">⚠️</span>
                        <div>
                            <p className="text-sm font-bold text-amber-800">
                                {t('completeProfileTitle') || 'உங்கள் சுயவிவரத்தை நிரப்பவும் · Complete Your Profile'}
                            </p>
                            <p className="mt-1 text-sm leading-relaxed text-amber-700">
                                {t('completeProfileDesc') || 'பொருத்தமான திருமண பரிந்துரைகளைப் பெற உங்கள் பாலினம் (Gender) சுயவிவரத்தில் சேர்க்கவும். Matches are shown based on opposite gender — please add your gender in your profile.'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => window.location.href = '/member/profile'}
                        className="w-fit rounded-full bg-amber-600 px-5 py-2 text-sm font-bold text-white hover:bg-amber-700 transition"
                    >
                        {t('goToProfile') || 'சுயவிவரம் திருத்து · Edit Profile →'}
                    </button>
                </div>
            )}

            {/* ── Tab bar + top controls ── */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Tabs */}
                <div className="inline-flex flex-wrap rounded-full border border-borderColor bg-white p-1 text-sm font-bold">
                    <button type="button" onClick={() => setTab("browse")}
                        className={`rounded-full px-4 py-2 transition ${tab === "browse" ? "bg-primeColor text-white" : "text-tColor"}`}>
                        {t("browseProfiles") || "Browse Profiles"} ({suggestions.length})
                    </button>
                    <button type="button" onClick={() => setTab("myMatches")}
                        className={`rounded-full px-4 py-2 transition ${tab === "myMatches" ? "bg-primeColor text-white" : "text-tColor"}`}>
                        {t("myMatches") || "My Matches"} ({myMatches.length})
                    </button>
                    <button type="button" onClick={() => setTab("shortlist")}
                        className={`rounded-full px-4 py-2 transition ${tab === "shortlist" ? "bg-primeColor text-white" : "text-tColor"}`}>
                        {t("shortlist")} ({shortlist.length})
                    </button>
                    <button type="button" onClick={() => setTab("blocked")}
                        className={`rounded-full px-4 py-2 transition ${tab === "blocked" ? "bg-primeColor text-white" : "text-tColor"}`}>
                        {t("blocked")} ({blocked.length})
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    {/* Search */}
                    <div className="relative">
                        <HiOutlineMagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tpColor" />
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("searchPlaceholder")}
                            className="w-48 md:w-56 rounded-full border border-borderColor bg-white py-2 pl-9 pr-4 text-sm text-tColor outline-none placeholder:text-tpColor/70 focus:border-primeColor" />
                    </div>
                    {/* View mode toggle */}
                    <div className="inline-flex rounded-full border border-borderColor bg-white p-0.5">
                        <button type="button" title="Classic view" onClick={() => setViewMode("classic")}
                            className={`flex h-8 w-8 items-center justify-center rounded-full transition ${viewMode === "classic" ? "bg-primeColor text-white" : "text-tpColor hover:text-primeColor"}`}>
                            <HiOutlineListBullet className="h-4 w-4" />
                        </button>
                        <button type="button" title="Grid view" onClick={() => setViewMode("grid")}
                            className={`flex h-8 w-8 items-center justify-center rounded-full transition ${viewMode === "grid" ? "bg-primeColor text-white" : "text-tpColor hover:text-primeColor"}`}>
                            <HiOutlineSquares2X2 className="h-4 w-4" />
                        </button>
                    </div>
                    {/* Mobile-only filter button */}
                    <button type="button" onClick={() => setFiltersOpen(o => !o)}
                        className={`md:hidden relative inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition
                            ${activeFilterCount > 0 ? "border-primeColor bg-primeSoft text-primeColor" : "border-borderColor bg-white text-tColor"}`}>
                        <HiOutlineFunnel className="h-4 w-4" />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primeColor text-[10px] font-bold text-white">{activeFilterCount}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Two-column layout: sidebar + results ── */}
            <div className="flex gap-5 items-start">

                {/* ══════════════════════════════════════════════
                    LEFT FILTER SIDEBAR — desktop only
                    Hidden on mobile (mobile uses drawer below)
                    ══════════════════════════════════════════════ */}
                <aside className="hidden md:block w-60 xl:w-64 shrink-0">
                    <div className="sticky top-20 rounded-2xl border border-borderColor bg-white shadow-sm overflow-hidden">

                        {/* Sidebar header */}
                        <div className="flex items-center justify-between border-b border-borderColor bg-slate-50 px-4 py-3">
                            <div className="flex items-center gap-2">
                                <HiOutlineFunnel className="h-4 w-4 text-primeColor" />
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">Filter Matches</p>
                            </div>
                            {activeFilterCount > 0 && (
                                <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)}
                                    className="text-[11px] font-bold text-red-400 hover:text-red-600 transition underline">
                                    Clear ({activeFilterCount})
                                </button>
                            )}
                        </div>

                        {/* Scrollable filter sections */}
                        <div className="divide-y divide-borderColor overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>

                            {/* Age Range */}
                            <div className="px-4 py-4 space-y-2">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Age Range</p>
                                <div className="flex items-center gap-2">
                                    <input type="number" min="18" max="80" placeholder="Min"
                                        value={filters.ageMin}
                                        onChange={e => setFilters(f => ({ ...f, ageMin: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-primeColor focus:bg-white transition" />
                                    <span className="text-xs text-slate-400 shrink-0">–</span>
                                    <input type="number" min="18" max="80" placeholder="Max"
                                        value={filters.ageMax}
                                        onChange={e => setFilters(f => ({ ...f, ageMax: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-primeColor focus:bg-white transition" />
                                </div>
                            </div>

                            {/* Location */}
                            <div className="px-4 py-4 space-y-2">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1">
                                    <HiOutlineMapPin className="h-3 w-3" /> Location
                                </p>
                                {uniqueValues.cities.length > 0 ? (
                                    <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                            <input type="radio" name="sb-city" value="" checked={!filters.city}
                                                onChange={() => setFilters(f => ({ ...f, city: "" }))}
                                                className="accent-primeColor" />
                                            <span className="text-sm text-slate-700 group-hover:text-primeColor transition">Any City</span>
                                        </label>
                                        {uniqueValues.cities.map(c => (
                                            <label key={c} className="flex items-center gap-2 cursor-pointer group">
                                                <input type="radio" name="sb-city" value={c} checked={filters.city === c}
                                                    onChange={() => setFilters(f => ({ ...f, city: c }))}
                                                    className="accent-primeColor" />
                                                <span className="text-sm text-slate-700 truncate group-hover:text-primeColor transition">{c}</span>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <input type="text" placeholder="Any city" value={filters.city}
                                        onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-primeColor focus:bg-white transition" />
                                )}
                            </div>

                            {/* Marital Status */}
                            {(uniqueValues.maritalStatuses.length > 0) && (
                                <div className="px-4 py-4 space-y-2">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Marital Status</p>
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                            <input type="radio" name="sb-marital" value="" checked={!filters.maritalStatus}
                                                onChange={() => setFilters(f => ({ ...f, maritalStatus: "" }))}
                                                className="accent-primeColor" />
                                            <span className="text-sm text-slate-700 group-hover:text-primeColor transition">Any</span>
                                        </label>
                                        {uniqueValues.maritalStatuses.map(s => (
                                            <label key={s} className="flex items-center gap-2 cursor-pointer group">
                                                <input type="radio" name="sb-marital" value={s} checked={filters.maritalStatus === s}
                                                    onChange={() => setFilters(f => ({ ...f, maritalStatus: s }))}
                                                    className="accent-primeColor" />
                                                <span className="text-sm text-slate-700 group-hover:text-primeColor transition">{s}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Profession */}
                            <div className="px-4 py-4 space-y-2">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1">
                                    <HiOutlineBriefcase className="h-3 w-3" /> Profession
                                </p>
                                {uniqueValues.professions.length > 0 ? (
                                    <select value={filters.profession}
                                        onChange={e => setFilters(f => ({ ...f, profession: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-primeColor focus:bg-white transition">
                                        <option value="">Any Profession</option>
                                        {uniqueValues.professions.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                ) : (
                                    <input type="text" placeholder="e.g. Engineer" value={filters.profession}
                                        onChange={e => setFilters(f => ({ ...f, profession: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-primeColor focus:bg-white transition" />
                                )}
                            </div>

                            {/* Annual Income */}
                            <div className="px-4 py-4 space-y-2">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1">
                                    <HiOutlineCurrencyRupee className="h-3 w-3" /> Annual Income
                                </p>
                                <div className="space-y-1.5">
                                    {SALARY_RANGES.map(r => (
                                        <label key={r.value} className="flex items-center gap-2 cursor-pointer group">
                                            <input type="radio" name="sb-salary" value={r.value} checked={filters.salaryRange === r.value}
                                                onChange={() => setFilters(f => ({ ...f, salaryRange: r.value }))}
                                                className="accent-primeColor" />
                                            <span className="text-sm text-slate-700 group-hover:text-primeColor transition">{r.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* ✦ Jathagam Filters */}
                            <div className="px-4 py-4 space-y-4 bg-primeSoft/20">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primeColor flex items-center gap-1.5">
                                    <HiOutlineSparkles className="h-3.5 w-3.5" /> Jathagam Filters
                                </p>

                                {/* Star */}
                                {uniqueValues.stars.length > 0 && (
                                    <div className="space-y-1.5">
                                        <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
                                            <HiOutlineStar className="h-3 w-3" /> Star / Nakshatra
                                        </p>
                                        <select value={filters.star}
                                            onChange={e => setFilters(f => ({ ...f, star: e.target.value }))}
                                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primeColor transition">
                                            <option value="">Any Star</option>
                                            {uniqueValues.stars.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                )}

                                {/* Porutham verdict */}
                                <div className="space-y-2">
                                    <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
                                        <HiOutlineSparkles className="h-3 w-3" /> Porutham Match
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {["", "excellent", "good", "average", "incompatible"].map(v => {
                                            const active = filters.poruthamVerdict === v
                                            const colors = {
                                                excellent:    active ? "bg-[#1a8a3a] text-white border-[#1a8a3a]" : "bg-[#ecf8ef] text-[#1a8a3a] border-[#cfe8d4] hover:border-[#1a8a3a]",
                                                good:         active ? "bg-[#225ec1] text-white border-[#225ec1]" : "bg-[#eef5ff] text-[#225ec1] border-[#cfe1ff] hover:border-[#225ec1]",
                                                average:      active ? "bg-[#a07414] text-white border-[#a07414]" : "bg-[#fff8e6] text-[#a07414] border-[#f7e3a3] hover:border-[#a07414]",
                                                incompatible: active ? "bg-[#a03e3e] text-white border-[#a03e3e]" : "bg-[#fff5f5] text-[#a03e3e] border-[#efcaca] hover:border-[#a03e3e]",
                                                "":           active ? "bg-primeColor text-white border-primeColor" : "bg-slate-50 text-slate-600 border-slate-200 hover:border-primeColor/60",
                                            }
                                            return (
                                                <button key={v} type="button"
                                                    onClick={() => setFilters(f => ({ ...f, poruthamVerdict: v }))}
                                                    className={`rounded-full border px-2.5 py-1 text-xs font-bold transition ${colors[v] || colors[""]}`}>
                                                    {v === "" ? "Any" : v.charAt(0).toUpperCase() + v.slice(1)}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Result count footer */}
                            <div className="bg-slate-50 px-4 py-3 text-center">
                                <p className="text-xs text-slate-500">
                                    <span className="font-bold text-tColor">{filtered.length}</span> of {list.length} matches
                                </p>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* ══════════════════════════════════════════════
                    MOBILE FILTER DRAWER
                    Slides up from bottom when filter button clicked
                    ══════════════════════════════════════════════ */}
                <AnimatePresence>
                    {filtersOpen && (
                        <motion.div
                            className="fixed inset-0 z-[100] md:hidden flex flex-col justify-end"
                            style={{ background: "rgba(0,0,0,0.5)" }}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={() => setFiltersOpen(false)}
                        >
                            <motion.div
                                className="max-h-[88vh] overflow-y-auto rounded-t-2xl bg-white"
                                initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 28, stiffness: 260 }}
                                onClick={e => e.stopPropagation()}
                            >
                                {/* Drawer header */}
                                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-borderColor bg-white px-5 py-4">
                                    <div className="flex items-center gap-2">
                                        <HiOutlineFunnel className="h-4 w-4 text-primeColor" />
                                        <p className="text-sm font-bold text-tColor">Filter Matches</p>
                                        {activeFilterCount > 0 && (
                                            <span className="rounded-full bg-primeColor px-2 py-0.5 text-[10px] font-bold text-white">{activeFilterCount}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {activeFilterCount > 0 && (
                                            <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)}
                                                className="text-xs font-bold text-red-500 underline">Clear all</button>
                                        )}
                                        <button type="button" onClick={() => setFiltersOpen(false)}
                                            className="flex h-8 w-8 items-center justify-center rounded-full border border-borderColor text-slate-500">
                                            <HiOutlineXMark className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Drawer filter body */}
                                <div className="divide-y divide-borderColor px-5">
                                    <div className="py-4 space-y-2">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Age Range</p>
                                        <div className="flex items-center gap-2">
                                            <input type="number" min="18" max="80" placeholder="Min age" value={filters.ageMin}
                                                onChange={e => setFilters(f => ({ ...f, ageMin: e.target.value }))}
                                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor" />
                                            <span className="text-xs text-slate-400 shrink-0">–</span>
                                            <input type="number" min="18" max="80" placeholder="Max age" value={filters.ageMax}
                                                onChange={e => setFilters(f => ({ ...f, ageMax: e.target.value }))}
                                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor" />
                                        </div>
                                    </div>
                                    <div className="py-4 space-y-2">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Location</p>
                                        <select value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor">
                                            <option value="">Any City</option>
                                            {uniqueValues.cities.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="py-4 space-y-2">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Marital Status</p>
                                        <select value={filters.maritalStatus} onChange={e => setFilters(f => ({ ...f, maritalStatus: e.target.value }))}
                                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor">
                                            <option value="">Any Status</option>
                                            {uniqueValues.maritalStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="py-4 space-y-2">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Profession</p>
                                        {uniqueValues.professions.length > 0 ? (
                                            <select value={filters.profession} onChange={e => setFilters(f => ({ ...f, profession: e.target.value }))}
                                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor">
                                                <option value="">Any Profession</option>
                                                {uniqueValues.professions.map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        ) : (
                                            <input type="text" placeholder="e.g. Engineer" value={filters.profession}
                                                onChange={e => setFilters(f => ({ ...f, profession: e.target.value }))}
                                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor" />
                                        )}
                                    </div>
                                    <div className="py-4 space-y-2">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Annual Income</p>
                                        <div className="flex flex-wrap gap-2">
                                            {SALARY_RANGES.map(r => (
                                                <button key={r.value} type="button"
                                                    onClick={() => setFilters(f => ({ ...f, salaryRange: r.value }))}
                                                    className={`rounded-full border px-3 py-1.5 text-xs font-bold transition
                                                        ${filters.salaryRange === r.value ? "border-primeColor bg-primeColor text-white" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                                                    {r.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="py-4 space-y-3">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primeColor flex items-center gap-1.5">
                                            <HiOutlineSparkles className="h-3.5 w-3.5" /> Jathagam Filters
                                        </p>
                                        {uniqueValues.stars.length > 0 && (
                                            <select value={filters.star} onChange={e => setFilters(f => ({ ...f, star: e.target.value }))}
                                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primeColor">
                                                <option value="">Any Star</option>
                                                {uniqueValues.stars.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        )}
                                        <p className="text-[11px] font-semibold text-slate-500">Porutham Match</p>
                                        <div className="flex flex-wrap gap-2">
                                            {["", "excellent", "good", "average", "incompatible"].map(v => {
                                                const active = filters.poruthamVerdict === v
                                                const colors = {
                                                    excellent:    active ? "bg-[#1a8a3a] text-white border-[#1a8a3a]" : "bg-[#ecf8ef] text-[#1a8a3a] border-[#cfe8d4]",
                                                    good:         active ? "bg-[#225ec1] text-white border-[#225ec1]" : "bg-[#eef5ff] text-[#225ec1] border-[#cfe1ff]",
                                                    average:      active ? "bg-[#a07414] text-white border-[#a07414]" : "bg-[#fff8e6] text-[#a07414] border-[#f7e3a3]",
                                                    incompatible: active ? "bg-[#a03e3e] text-white border-[#a03e3e]" : "bg-[#fff5f5] text-[#a03e3e] border-[#efcaca]",
                                                    "":           active ? "bg-primeColor text-white border-primeColor" : "bg-slate-50 text-slate-600 border-slate-200",
                                                }
                                                return (
                                                    <button key={v} type="button"
                                                        onClick={() => setFilters(f => ({ ...f, poruthamVerdict: v }))}
                                                        className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${colors[v] || colors[""]}`}>
                                                        {v === "" ? "Any" : v.charAt(0).toUpperCase() + v.slice(1)}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* Sticky apply button */}
                                <div className="sticky bottom-0 border-t border-borderColor bg-white px-5 py-4">
                                    <button type="button" onClick={() => setFiltersOpen(false)}
                                        className="w-full rounded-2xl bg-primeColor py-3 text-sm font-bold text-white">
                                        Show {filtered.length} match{filtered.length !== 1 ? "es" : ""}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ══════════════════════════════════════════════
                    RIGHT COLUMN — result cards
                    ══════════════════════════════════════════════ */}
                <div className="flex-1 min-w-0 space-y-4">

                    {/* Result count + active filter chips */}
                    <div className="flex flex-wrap items-center gap-2 min-h-[28px]">
                        <p className="text-sm text-tpColor shrink-0">
                            <span className="font-bold text-tColor">{filtered.length}</span> match{filtered.length !== 1 ? "es" : ""} found
                        </p>
                        {filters.ageMin && <FilterChip label={`Age ≥ ${filters.ageMin}`} onRemove={() => setFilters(f => ({ ...f, ageMin: "" }))} />}
                        {filters.ageMax && <FilterChip label={`Age ≤ ${filters.ageMax}`} onRemove={() => setFilters(f => ({ ...f, ageMax: "" }))} />}
                        {filters.city && <FilterChip label={`📍 ${filters.city}`} onRemove={() => setFilters(f => ({ ...f, city: "" }))} />}
                        {filters.maritalStatus && <FilterChip label={filters.maritalStatus} onRemove={() => setFilters(f => ({ ...f, maritalStatus: "" }))} />}
                        {filters.profession && <FilterChip label={filters.profession} onRemove={() => setFilters(f => ({ ...f, profession: "" }))} />}
                        {filters.salaryRange && <FilterChip label={SALARY_RANGES.find(r => r.value === filters.salaryRange)?.label || filters.salaryRange} onRemove={() => setFilters(f => ({ ...f, salaryRange: "" }))} />}
                        {filters.star && <FilterChip label={`★ ${filters.star}`} onRemove={() => setFilters(f => ({ ...f, star: "" }))} />}
                        {filters.poruthamVerdict && <FilterChip label={`Match: ${filters.poruthamVerdict}`} onRemove={() => setFilters(f => ({ ...f, poruthamVerdict: "" }))} />}
                    </div>

                    {loading && (
                        <div className="rounded-2xl border border-borderColor bg-white p-6 text-sm text-tpColor">{t("loadingMatches")}</div>
                    )}
                    {!loading && filtered.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-borderColor bg-lightGrey/30 p-8 text-center text-sm text-tpColor">
                            {(tab === "browse" || tab === "myMatches") && (t("noSuggestions") || "No profiles found.")}
                            {tab === "shortlist" && t("noShortlist")}
                            {tab === "blocked" && t("noBlocked")}
                        </div>
                    )}

                    {/* Rank badge row — rendered above the card in both views */}
                    {/* My Matches — classic (list) view */}
                    {tab === "myMatches" && !loading && filtered.length > 0 && viewMode !== "grid" && (
                        <div className="flex flex-col gap-4">
                            {filtered.map((profile, idx) => (
                                <div key={profile._id} className="relative">
                                    <div className="absolute -top-2.5 left-4 z-10 flex flex-wrap items-center gap-2">
                                        <span className="rounded-full bg-primeColor px-2.5 py-0.5 text-[11px] font-bold text-white shadow">
                                            #{idx + 1} Match
                                        </span>
                                        {profile.__matchScore > 0 && (
                                            <span className="rounded-full border border-[#cfe8d4] bg-[#f2fbf4] px-2.5 py-0.5 text-[11px] font-bold text-[#1a8a3a] shadow">
                                                Score {profile.__matchScore}
                                            </span>
                                        )}
                                        {(profile.__matchReasons || []).map(r => (
                                            <span key={r} className="rounded-full border border-[#cfe1ff] bg-[#eef5ff] px-2.5 py-0.5 text-[11px] font-semibold text-[#225ec1] shadow">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="pt-3">
                                        <ClassicMatchCard profile={profile} mode="browse"
                                            shortlisted={shortlistedIds.has(profile._id)}
                                            onShortlist={toggleShortlist} onView={viewProfile}
                                            onComputePorutham={computeDetailedPorutham}
                                            onBlock={blockProfile} onUnblock={unblockProfile}
                                            onChat={openChat} onReport={(p) => setReportTarget(p)} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* My Matches — grid view */}
                    {tab === "myMatches" && !loading && filtered.length > 0 && viewMode === "grid" && (
                        <div className="grid gap-6 sm:grid-cols-2">
                            {filtered.map((profile, idx) => (
                                <div key={profile._id} className="relative">
                                    <div className="absolute -top-2.5 left-3 z-10 flex flex-wrap items-center gap-1.5">
                                        <span className="rounded-full bg-primeColor px-2 py-0.5 text-[10px] font-bold text-white shadow">
                                            #{idx + 1}
                                        </span>
                                        {profile.__matchScore > 0 && (
                                            <span className="rounded-full border border-[#cfe8d4] bg-[#f2fbf4] px-2 py-0.5 text-[10px] font-bold text-[#1a8a3a] shadow">
                                                Score {profile.__matchScore}
                                            </span>
                                        )}
                                        {(profile.__matchReasons || []).slice(0, 2).map(r => (
                                            <span key={r} className="rounded-full border border-[#cfe1ff] bg-[#eef5ff] px-2 py-0.5 text-[10px] font-semibold text-[#225ec1] shadow">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="pt-3">
                                        <MatchCard profile={profile} mode="browse"
                                            shortlisted={shortlistedIds.has(profile._id)}
                                            onShortlist={toggleShortlist} onView={viewProfile}
                                            onComputePorutham={computeDetailedPorutham}
                                            onBlock={blockProfile} onUnblock={unblockProfile}
                                            onChat={openChat} onReport={(p) => setReportTarget(p)} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Classic view (browse / shortlist / blocked) */}
                    {tab !== "myMatches" && viewMode !== "grid" && (
                        <div className="flex flex-col gap-4">
                            {filtered.map((profile) => (
                                <ClassicMatchCard key={profile._id} profile={profile} mode={tab}
                                    shortlisted={shortlistedIds.has(profile._id)}
                                    onShortlist={toggleShortlist} onView={viewProfile}
                                    onComputePorutham={computeDetailedPorutham}
                                    onBlock={blockProfile} onUnblock={unblockProfile}
                                    onChat={openChat} onReport={(p) => setReportTarget(p)} />
                            ))}
                        </div>
                    )}

                    {/* Grid view (browse / shortlist / blocked) */}
                    {tab !== "myMatches" && viewMode === "grid" && (
                        <div className="grid gap-4 sm:grid-cols-2">
                            {filtered.map((profile) => (
                                <MatchCard key={profile._id} profile={profile} mode={tab}
                                    shortlisted={shortlistedIds.has(profile._id)}
                                    onShortlist={toggleShortlist} onView={viewProfile}
                                    onComputePorutham={computeDetailedPorutham}
                                    onBlock={blockProfile} onUnblock={unblockProfile}
                                    onChat={openChat} onReport={(p) => setReportTarget(p)} />
                            ))}
                        </div>
                    )}
                </div>
            </div>{/* end two-column */}

            <ReportUserDialog
                open={!!reportTarget}
                target={reportTarget}
                onClose={() => setReportTarget(null)}
                onReported={() => fetchAll()}
            />

            {poruthamModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPoruthamModal(null)}>
                    <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-4 flex items-start justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">{t("poruthamDetail")}</p>
                                <h3 className="mt-1 text-xl font-semibold text-tColor">{formatName(poruthamModal.profile)}</h3>
                            </div>
                            <button onClick={() => setPoruthamModal(null)} className="text-2xl text-tpColor hover:text-tColor">×</button>
                        </div>
                        {poruthamModal.error && <p className="rounded-md bg-[#fff5f5] p-3 text-sm text-[#a03e3e]">{poruthamModal.error}</p>}
                        {poruthamModal.result && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between rounded-xl bg-lightGrey/40 p-4">
                                    <div className="text-sm text-tpColor">
                                        <div>{t("bride")}: <strong className="text-tColor">{poruthamModal.result.bride.name} ({poruthamModal.result.bride.tName})</strong></div>
                                        <div className="mt-1">{t("groom")}: <strong className="text-tColor">{poruthamModal.result.groom.name} ({poruthamModal.result.groom.tName})</strong></div>
                                    </div>
                                    <PoruthamBadge porutham={poruthamModal.result} />
                                </div>
                                <ul className="space-y-2">
                                    {poruthamModal.result.breakdown.map((p) => (
                                        <li key={p.name} className={`rounded-lg border px-4 py-3 text-sm ${p.score ? "border-[#cfe8d4] bg-[#f2fbf4]" : "border-[#efcaca] bg-[#fff5f5]"}`}>
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold text-tColor">{p.name} <span className="text-tpColor">({p.tName})</span></span>
                                                <span className={p.score ? "text-[#1a8a3a]" : "text-[#a03e3e]"}>{p.score}/{p.max}</span>
                                            </div>
                                            <p className="mt-1 text-tpColor">{p.explanation}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </motion.div>

        {/* ── Profile View Slide-Over (tabbed) ─────────────────────────── */}
        <AnimatePresence onExitComplete={() => { document.body.style.overflow = "" }}>
            {viewingProfile && (
                <motion.div
                    className="fixed inset-0 z-[80] flex justify-end bg-slate-950/45 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    // pointerEvents: "none" fires immediately so the backdrop stops
                    // intercepting clicks (including the top nav bar) the moment the
                    // close animation begins — before the element is unmounted.
                    exit={{ opacity: 0, pointerEvents: "none" }}
                    onClick={() => setViewingProfile(null)}
                >
                    <motion.aside
                        className="flex h-full w-full max-w-2xl flex-col overflow-hidden border-l border-borderColor bg-pColor shadow-2xl"
                        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 260 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center gap-3 justify-between border-b border-borderColor bg-white px-5 py-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <ProfileAvatar profile={viewingProfile} size="sm" />
                                <div className="min-w-0">
                                    <h2 className="truncate text-base font-semibold text-tColor">
                                        {lang === 'ta' && slideTamilName ? slideTamilName : formatName(viewingProfile)}
                                    </h2>
                                    <p className="text-xs text-tpColor">
                                        {[viewingProfile.refId && `${t("ref")} ${viewingProfile.refId}`, viewingProfile.gender || viewingProfile.profileType, getStar(viewingProfile)].filter(Boolean).join(" · ")}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => toggleShortlist(viewingProfile)}
                                    className={`flex h-9 w-9 items-center justify-center rounded-full border transition ${
                                        shortlistedIds.has(viewingProfile._id)
                                            ? "border-[#efcaca] bg-[#fff5f5] text-[#c04a4a]"
                                            : "border-borderColor bg-white text-tpColor hover:border-primeColor hover:text-primeColor"
                                    }`}
                                    title={shortlistedIds.has(viewingProfile._id) ? t("removeShortlist") : t("addShortlist")}
                                >
                                    {shortlistedIds.has(viewingProfile._id) ? <HiHeart className="h-4 w-4" /> : <HiOutlineHeart className="h-4 w-4" />}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => printProfile(viewingProfile)}
                                    title={t("printTip")}
                                    className="flex h-9 w-9 items-center justify-center rounded-full border border-borderColor bg-white text-tpColor transition hover:border-primeColor hover:text-primeColor"
                                >
                                    <HiOutlinePrinter className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewingProfile(null)}
                                    title={t("closeTip")}
                                    className="flex h-9 w-9 items-center justify-center rounded-full border border-borderColor bg-white text-tpColor transition hover:border-primeColor hover:text-primeColor"
                                >
                                    <HiOutlineXMark className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* Tab bar */}
                        <div className="flex border-b border-borderColor bg-white px-5">
                            {[
                                { key: "profile",   label: t("profileTab"),  Icon: HiOutlineUser,                tip: t("profileTabTip")  },
                                { key: "porutham",  label: t("poruthamTab"), Icon: HiOutlineSparkles,            tip: t("poruthamTabTip") },
                                { key: "chat",      label: t("chatTab"),     Icon: HiOutlineChatBubbleLeftRight, tip: t("chatTabTip")     },
                            ].map(({ key, label, Icon, tip }) => (
                                <button
                                    key={key}
                                    type="button"
                                    title={tip}
                                    onClick={() => setSlideTab(key)}
                                    className={`relative inline-flex items-center gap-2 px-5 py-3 text-sm font-bold transition ${
                                        slideTab === key
                                            ? "text-primeColor"
                                            : "text-tpColor hover:text-primeColor"
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {label}
                                    {slideTab === key && (
                                        <motion.div layoutId="slide-tab-line"
                                            className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primeColor" />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Tab body */}
                        <div className="flex-1 overflow-y-auto">

                            {/* ── PROFILE TAB ── */}
                            {slideTab === "profile" && (
                                <div className="space-y-0">
                                    {/* Photo viewer — full width */}
                                    <PhotoViewer profile={viewingProfile} />

                                    {/* All profile details */}
                                    <div className="p-5 space-y-4">

                                    {/* Quick porutham badge */}
                                    {viewingProfile.porutham && (
                                        <div className="rounded-2xl border border-borderColor bg-white p-4">
                                            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{t("jathagaPorutham")}</p>
                                            <PoruthamBadge porutham={viewingProfile.porutham} />
                                            {/* Breakdown chips — paid only (backend omits breakdown for free) */}
                                            {viewingProfile.porutham.breakdown ? (
                                                <div className="mt-3 flex flex-wrap gap-1">
                                                    {viewingProfile.porutham.breakdown.map((p) => (
                                                        <span key={p.name} className={`rounded px-2 py-0.5 text-[11px] font-semibold ${p.score ? "bg-[#ecf8ef] text-[#1a8a3a]" : "bg-[#fff5f5] text-[#a03e3e] line-through opacity-70"}`}>
                                                            {p.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : !amIPaid && (
                                                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                                                    <HiOutlineLockClosed className="h-3.5 w-3.5" />
                                                    {t("upgradeDescPorutham")}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* ── Basic Info ── */}
                                    <InfoCard title={t("basicInfo")}>
                                        {[
                                            { icon: HiOutlineUser,         label: t("gender"),             value: viewingProfile.gender || viewingProfile.profileType },
                                            { icon: HiOutlineCalendarDays, label: t("dateOfBirth"),        value: viewingProfile.dateOfBirth ? new Date(viewingProfile.dateOfBirth).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null },
                                            { icon: HiOutlineSparkles,     label: t("starNakshatra"),      value: getStar(viewingProfile) },
                                            { icon: HiOutlineUser,         label: t("religionCaste"),      value: [viewingProfile.religion, viewingProfile.caste, viewingProfile.subCaste].filter(Boolean).join(" · ") },
                                            { icon: HiOutlineUser,         label: t("motherTongueLabel"),  value: viewingProfile.motherTongue },
                                            { icon: HiOutlineUser,         label: t("maritalStatusLabel"), value: viewingProfile.maritalStatus },
                                            { icon: HiOutlineUser,         label: t("heightLabel"),        value: viewingProfile.heightInFeet || viewingProfile.height },
                                            { icon: HiOutlineUser,         label: t("weightLabel"),        value: viewingProfile.weight },
                                            { icon: HiOutlineUser,         label: t("complexionLabel"),    value: viewingProfile.complexion },
                                        ]}
                                    </InfoCard>

                                    {/* ── Education & Career ── */}
                                    <InfoCard title={t("educationCareerSection")}>
                                        {[
                                            { icon: HiOutlineAcademicCap, label: t("educationLabel"),  value: resolveText(viewingProfile.educationalQualification) || resolveText(viewingProfile.education) },
                                            { icon: HiOutlineBriefcase,   label: t("occupationLabel"), value: resolveText(viewingProfile.occupationDetail) || resolveText(viewingProfile.profession) || resolveText(viewingProfile.occupation) },
                                            { icon: HiOutlineBriefcase,   label: t("salaryLabel"),     value: viewingProfile.salary || viewingProfile.income },
                                            { icon: HiOutlineMapPin,      label: t("native"),          value: viewingProfile.native },
                                            { icon: HiOutlineMapPin,      label: t("currentCityLabel"),value: viewingProfile.city || viewingProfile.address?.city },
                                        ]}
                                    </InfoCard>

                                    {/* ── Family Details ── */}
                                    {(() => {
                                        const fi = viewingProfile.familyInformation || {}
                                        const fatherName = [fi.fatherFirstName, fi.fatherSurname].filter(Boolean).join(" ")
                                        const motherName = [fi.motherFirstName, fi.motherSurname].filter(Boolean).join(" ")
                                        const siblings   = fi.numberOfSibilings ?? fi.numberOfSiblings
                                        const maleSib    = fi.numberOfMaleSibilings ?? fi.numberOfMaleSiblings
                                        const femaleSib  = fi.numberOfFemaleSibilings ?? fi.numberOfFemaleSiblings
                                        const hasData    = [fatherName, motherName, fi.fatherOccupation,
                                            fi.motherOccupation, fi.fatherNative].some(Boolean)
                                            || siblings != null
                                        if (!hasData) return null
                                        return (
                                            <InfoCard title={t("familySection")}>
                                                {[
                                                    { icon: HiOutlineUsers, label: t("fatherNameLabel"),    value: fatherName },
                                                    { icon: HiOutlineUsers, label: t("fatherOccupation") || "Father's Occupation", value: fi.fatherOccupation },
                                                    { icon: HiOutlineUsers, label: t("motherNameLabel"),    value: motherName },
                                                    { icon: HiOutlineUsers, label: t("motherOccupation") || "Mother's Occupation", value: fi.motherOccupation },
                                                    { icon: HiOutlineUsers, label: t("fatherNative") || "Native",    value: fi.fatherNative },
                                                    { icon: HiOutlineUsers, label: t("siblingsLabel"),      value: siblings != null ? String(siblings) : null },
                                                    { icon: HiOutlineUsers, label: t("maleSiblings") || "Male Siblings",   value: maleSib != null ? String(maleSib) : null },
                                                    { icon: HiOutlineUsers, label: t("femaleSiblings") || "Female Siblings", value: femaleSib != null ? String(femaleSib) : null },
                                                ]}
                                            </InfoCard>
                                        )
                                    })()}

                                    {/* ── About / Expectations ── */}
                                    {(viewingProfile.additionalInformation?.expectation || viewingProfile.aboutMe) && (
                                        <div className="rounded-2xl border border-borderColor bg-white p-4">
                                            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{t("aboutSection")}</p>
                                            {viewingProfile.aboutMe && (
                                                <p className="text-sm leading-7 text-tpColor mb-2">{viewingProfile.aboutMe}</p>
                                            )}
                                            {viewingProfile.additionalInformation?.expectation && (
                                                <p className="text-sm leading-7 text-tpColor">{viewingProfile.additionalInformation.expectation}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* ── Expected / Partner Preference ── */}
                                    {(() => {
                                        const ai = viewingProfile.additionalInformation || {}
                                        const hasData = [ai.expectedOccupation, ai.expectedSalary,
                                            ai.expectedStatus, ai.expectation].some(Boolean)
                                            || ai.expectedZodiacSigns?.length || ai.expectedStars?.length
                                        if (!hasData) return null
                                        return (
                                            <InfoCard title={t("partnerPreference") || "Partner Preference"}>
                                                {[
                                                    { icon: HiOutlineBriefcase,   label: t("expectedOccupation") || "Expected Occupation", value: resolveOccupation(ai.expectedOccupation) },
                                                    { icon: HiOutlineBriefcase,   label: t("expectedSalary") || "Expected Salary",     value: ai.expectedSalary },
                                                    { icon: HiOutlineUser,        label: t("expectedStatus") || "Expected Status",     value: Array.isArray(ai.expectedStatus) ? ai.expectedStatus.map(s => (typeof s === 'string' ? s : s?.text || s?.value)).join(', ') : ai.expectedStatus },
                                                    { icon: HiOutlineSparkles,    label: t("expectedStars") || "Expected Stars",       value: Array.isArray(ai.expectedStars) ? ai.expectedStars.map(s => (typeof s === 'string' ? s : s?.text || s?.value)).join(', ') : ai.expectedStars },
                                                    { icon: HiOutlineUser,        label: t("expectations") || "Expectations",          value: ai.expectation },
                                                ]}
                                            </InfoCard>
                                        )
                                    })()}

                                    {/* ── Jathagam / Horoscope (paid only) ── */}
                                    {amIPaid ? (
                                        [viewingProfile.birthTime, viewingProfile.birthPlace,
                                         viewingProfile.zodiacSignId, viewingProfile.dhisaId,
                                         viewingProfile.horoscope].some(Boolean) && (
                                            <InfoCard title={t("jathagamSection")} accent="primeColor">
                                                {[
                                                    { icon: HiOutlineCalendarDays, label: t("birthTime"),  value: viewingProfile.birthTime },
                                                    { icon: HiOutlineMapPin,       label: t("birthPlace"), value: viewingProfile.birthPlace },
                                                    { icon: HiOutlineStar,         label: t("rasiLabel"),  value: viewingProfile.zodiacSignId },
                                                    { icon: HiOutlineSparkles,     label: t("dhisaiLabel"),value: viewingProfile.dhisaId },
                                                    { icon: HiOutlineUser,         label: "Horoscope",     value: viewingProfile.horoscope },
                                                ]}
                                            </InfoCard>
                                        )
                                    ) : (
                                        <LockedSection
                                            title={t("jathagamSection")}
                                            desc={t("upgradeDescJathagam")}
                                        />
                                    )}

                                    {/* ── Contact Info (paid only) ── */}
                                    {amIPaid ? (
                                        (viewingProfile.mobileNumber || viewingProfile.email) && (
                                            <div className="rounded-2xl border border-borderColor bg-white p-4 space-y-3">
                                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{t("contactSection")}</p>
                                                {viewingProfile.mobileNumber && (
                                                    <div className="flex items-center gap-3">
                                                        <HiOutlinePhone className="h-4 w-4 shrink-0 text-primeColor" />
                                                        <div>
                                                            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-tpColor/70">{t("phone")}</p>
                                                            <a href={`tel:${viewingProfile.mobileNumber}`}
                                                                className="text-sm font-bold text-primeColor hover:underline">
                                                                {viewingProfile.mobileNumber}
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                                {viewingProfile.email && (
                                                    <div className="flex items-center gap-3">
                                                        <HiOutlineEnvelope className="h-4 w-4 shrink-0 text-primeColor" />
                                                        <div>
                                                            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-tpColor/70">{t("emailLabel")}</p>
                                                            <a href={`mailto:${viewingProfile.email}`}
                                                                className="text-sm font-bold text-primeColor hover:underline break-all">
                                                                {viewingProfile.email}
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    ) : (
                                        <LockedSection
                                            title={t("contactSection")}
                                            desc={t("upgradeDescContact")}
                                        />
                                    )}

                                    </div>{/* end padded details */}
                                </div>
                            )}

                            {/* ── PORUTHAM TAB ── */}
                            {slideTab === "porutham" && (
                                <div className="p-5 space-y-4">
                                    {/* Free-plan lock — show above computing spinner */}
                                    {!amIPaid && (
                                        <LockedSection
                                            title={t("poruthamTab")}
                                            desc={t("upgradeDescPorutham")}
                                        />
                                    )}

                                    {amIPaid && slidePoruthamLoading && (
                                        <div className="flex items-center justify-center gap-2 py-16 text-sm text-tpColor">
                                            <HiOutlineArrowPath className="h-5 w-5 animate-spin text-primeColor" />
                                            {t("computingPorutham")}
                                        </div>
                                    )}
                                    {amIPaid && !slidePoruthamLoading && slidePorutham?.error && (
                                        <div className="rounded-2xl border border-[#efcaca] bg-[#fff5f5] p-4 text-sm text-[#a03e3e]">
                                            {slidePorutham.error}
                                            <p className="mt-1 text-xs">{t("starMissingError")}</p>
                                        </div>
                                    )}
                                    {amIPaid && !slidePoruthamLoading && slidePorutham?.result && (
                                        <>
                                            <div className="rounded-2xl border border-borderColor bg-white p-4 space-y-3">
                                                <div className="flex items-center justify-between flex-wrap gap-2">
                                                    <div className="text-sm text-tpColor space-y-1">
                                                        <div>{t("bride")}: <strong className="text-tColor">{slidePorutham.result.bride?.name} <span className="text-tpColor font-normal">({slidePorutham.result.bride?.tName})</span></strong></div>
                                                        <div>{t("groom")}: <strong className="text-tColor">{slidePorutham.result.groom?.name} <span className="text-tpColor font-normal">({slidePorutham.result.groom?.tName})</span></strong></div>
                                                    </div>
                                                    <PoruthamBadge porutham={slidePorutham.result} />
                                                </div>
                                                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                                    <div className="h-full rounded-full bg-primeColor transition-all"
                                                        style={{ width: `${Math.round((slidePorutham.result.total / slidePorutham.result.max) * 100)}%` }} />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                {slidePorutham.result.breakdown?.map((p) => (
                                                    <div key={p.name} className={`rounded-2xl border px-4 py-3 text-sm ${p.score ? "border-[#cfe8d4] bg-[#f2fbf4]" : "border-[#efcaca] bg-[#fff5f5]"}`}>
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-bold text-tColor">{p.name} <span className="font-normal text-tpColor">({p.tName})</span></span>
                                                            <span className={`text-xs font-bold ${p.score ? "text-[#1a8a3a]" : "text-[#a03e3e]"}`}>{p.score}/{p.max}</span>
                                                        </div>
                                                        <p className="mt-1 text-xs text-tpColor">{p.explanation}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── CHAT TAB ── */}
                            {slideTab === "chat" && (
                                <div className="flex h-full flex-col">
                                    <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" style={{ maxHeight: "calc(100vh - 260px)" }}>
                                        {slideChatLoading && slideMessages.length === 0 && (
                                            <div className="flex items-center justify-center gap-2 py-10 text-sm text-tpColor">
                                                <HiOutlineArrowPath className="h-5 w-5 animate-spin text-primeColor" />
                                            </div>
                                        )}
                                        {!slideChatLoading && slideMessages.length === 0 && (
                                            <div className="rounded-xl bg-lightGrey/40 p-4 text-center text-sm text-tpColor">
                                                {t("sayHello")}
                                            </div>
                                        )}
                                        {slideMessages.map((m) => {
                                            const mine = m.from === userDetails?._id
                                            return (
                                                <div key={m._id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm ${mine ? "bg-primeColor text-white" : "bg-white border border-borderColor text-tColor"}`}>
                                                        <p className="whitespace-pre-wrap break-words">{m.body}</p>
                                                        <p className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-tpColor"}`}>
                                                            {m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                                                            {mine && m.readAt ? ` · ${t("readReceipt")}` : ""}
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div ref={chatEndRef} />
                                    </div>
                                    <form onSubmit={slideSend} className="flex items-center gap-2 border-t border-borderColor bg-white p-3">
                                        <input
                                            type="text"
                                            value={slideSendText}
                                            onChange={(e) => setSlideSendText(e.target.value)}
                                            placeholder={t("typingPlaceholder")}
                                            className="flex-1 rounded-full border border-borderColor bg-lightGrey/30 px-4 py-2 text-sm outline-none focus:border-primeColor"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!slideSendText.trim() || slideSending}
                                            title="Send message"
                                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primeColor text-white transition hover:bg-primeDark disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <HiOutlinePaperAirplane className="h-5 w-5" />
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                    </motion.aside>
                </motion.div>
            )}
        </AnimatePresence>
        </>
    )
}

export default MemberMatches
